import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { Card } from '@shared/components/ui/card';
import { cn } from '@shared/lib/utils';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface MetricCardProps {
  title: string;
  /** Extra classes for the title, e.g. uppercase hero-stat labels. */
  titleClassName?: string;
  value: ReactNode;
  unit?: string;
  badge?: ReactNode;
  description?: ReactNode;
  /**
   * Secondary line under the description, for a period-on-period change.
   * Ignored when the card is locked or has no value.
   */
  trend?: ReactNode;
  headerAction?: ReactNode;
  /**
   * `default` puts the title above the value; `stat` puts the value first
   * with the title as a small uppercase label beneath it.
   */
  variant?: 'default' | 'stat';
  /**
   * Wraps the card in a link to this path, with an arrow affordance and
   * hover styles.
   */
  to?: string;
  /**
   * Renders the card as a padlocked "Not enabled" tile — value, unit, badge
   * and description are ignored, and the card is not clickable.
   */
  locked?: boolean;
  /**
   * Caption shown in place of the default "Not enabled" when `locked` — for an
   * upsell prompt rather than a bare disabled state.
   */
  lockedDescription?: ReactNode;
  /**
   * Small teal drill-through link at the bottom of the card. Not rendered
   * when `locked`.
   */
  footerLink?: { label: string; to: string };
  className?: string;
}

export function MetricCard({
  title,
  titleClassName,
  value,
  unit,
  badge,
  description,
  trend,
  headerAction,
  variant = 'default',
  to,
  locked = false,
  lockedDescription,
  footerLink,
  className,
}: MetricCardProps) {
  const linked = !!to && !locked;

  const action = locked ? (
    <Icons.lock className="size-3.5 text-muted-foreground" />
  ) : linked ? (
    <Icons.arrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
  ) : (
    headerAction
  );

  // Missing data renders as a single subtle hyphen instead of the
  // full-strength primary value, so empty dashboards read calmly.
  const isMissing = locked || value == null || value === '-';
  const valueRow = (
    <div
      className={cn(
        'flex flex-wrap items-baseline gap-x-2',
        variant === 'default' && 'mt-3'
      )}
    >
      <Text
        className={cn(
          'text-4xl font-medium leading-none',
          isMissing ? 'text-gray-300' : 'text-primary-800'
        )}
      >
        {isMissing ? '-' : value}
      </Text>
      {!isMissing && unit && (
        <Text className="text-sm font-medium text-primary-500">{unit}</Text>
      )}
      {/* Baseline-aligned so the badge reads inline with the unit. */}
      {!isMissing && badge && <span>{badge}</span>}
    </div>
  );

  const descriptionRow = (locked || description) && (
    <Text variant="subtle" className="mt-1.5 text-sm">
      {locked ? (lockedDescription ?? 'Not enabled') : description}
    </Text>
  );

  // A change figure is meaningless without a value to change from.
  const trendRow = !isMissing && trend && <div className="mt-1">{trend}</div>;

  const footerLinkRow = !locked && footerLink && (
    <Link
      to={footerLink.to}
      className="group/footer-link mt-1.5 flex items-center gap-1 self-start text-sm font-medium text-teal-600 hover:text-teal-700"
    >
      {footerLink.label}
      <Icons.chevronRight className="size-3.5 shrink-0 transition-transform group-hover/footer-link:translate-x-0.5" />
    </Link>
  );

  const card = (
    <Card
      className={cn(
        'flex flex-col p-5',
        locked && 'opacity-70',
        linked && 'h-full transition-shadow group-hover:shadow-md',
        className
      )}
    >
      {variant === 'stat' ? (
        <>
          <div className="flex items-start justify-between gap-2">
            {valueRow}
            {action && <div className="shrink-0">{action}</div>}
          </div>
          <Text
            className={cn(
              'mt-1 text-sm font-semibold text-primary-700',
              titleClassName
            )}
          >
            {title}
          </Text>
          {descriptionRow}
          {trendRow}
          {footerLinkRow}
        </>
      ) : (
        <>
          <div className="flex items-start justify-between gap-2">
            <Text
              className={cn(
                'text-sm font-semibold text-primary-700',
                titleClassName
              )}
            >
              {title}
            </Text>
            {action && <div className="shrink-0">{action}</div>}
          </div>
          {valueRow}
          {descriptionRow}
          {trendRow}
          {footerLinkRow}
        </>
      )}
    </Card>
  );

  if (linked) {
    return (
      <Link to={to} className="group">
        {card}
      </Link>
    );
  }
  return card;
}
